/**
 * ReviewActionModal Component
 * Adapted from Tim-Combo ApprovalDetailModal for EA-Agile standards review
 *
 * Displays draft standard details and allows approve/reject/request changes
 */

import React, { useCallback, useState } from 'react';
import { FiCheckCircle, FiClock, FiX, FiXCircle, FiAlertTriangle } from 'react-icons/fi';
import { HiExclamationCircle } from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';
import type { StandardReviewItem } from '../pages/StandardsReviewPage';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface ReviewActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  review: StandardReviewItem | null;
  onActionComplete?: () => void;
  onNavigateNext?: () => void;
  onNavigatePrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

const ReviewActionModal: React.FC<ReviewActionModalProps> = ({
  isOpen,
  onClose,
  review,
  onActionComplete,
  onNavigateNext,
  onNavigatePrevious,
  hasNext = false,
  hasPrevious = false
}) => {
  const { user, accessToken } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Handle review action (approve/reject/request changes)
  const handleReviewAction = useCallback(async (action: 'approved' | 'rejected' | 'changes_requested') => {
    if (!review || !user) {
      setError('Missing required information for review action');
      return;
    }

    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      console.log(`Processing ${action} for review:`, review.review_id);

      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.STANDARD_REVIEW.UPDATE,
        'PUT',
        {
          review_id: review.review_id,
          status: action,
          review_comment: comment.trim() || '',
          reviewer_email: user.email
        }
      );

      if (isSuccessResponse(response)) {
        console.log(`${action} completed successfully`);
        setSuccess(`Standard ${action === 'approved' ? 'approved' : action === 'rejected' ? 'rejected' : 'sent back for changes'} successfully!`);

        // Clear form
        setComment('');

        // Notify parent of completion
        if (onActionComplete) {
          onActionComplete();
        }
      } else {
        throw new Error(response.message || `Failed to ${action} standard`);
      }

    } catch (err: unknown) {
      console.error(`Error ${action} review:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${action} standard. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  }, [review, user, comment, onActionComplete]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'normal': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (!isOpen || !review) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-3xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor((review as any).priority)}`}>
                  {(review as any).priority?.toUpperCase() || 'NORMAL'}
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Standard Review</h2>
              </div>

              {/* Navigation and close */}
              <div className="flex items-center space-x-2">
                {hasPrevious && (
                  <button
                    onClick={onNavigatePrevious}
                    disabled={isProcessing}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    ← Previous
                  </button>
                )}
                {hasNext && (
                  <button
                    onClick={onNavigateNext}
                    disabled={isProcessing}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next →
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {/* Success/Error messages */}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start">
                <FiCheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-2" />
                <p className="text-sm text-green-800">{success}</p>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <HiExclamationCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Standard details */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {review.standard_title}
                </h3>
                <div className="flex items-center space-x-3 text-sm text-gray-600">
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                    {review.standard_type}
                  </span>
                  <span>{review.focus_category}</span>
                  <span>•</span>
                  <span>Round {review.review_round}</span>
                </div>
              </div>

              {/* Linked incident */}
              {review.incident_title && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <FiAlertTriangle className="mr-2 text-yellow-600" />
                    Source Incident
                  </h4>
                  <p className="text-sm text-gray-700">{review.incident_title}</p>
                  {review.root_cause_summary && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-gray-600">Root Cause:</p>
                      <p className="text-sm text-gray-700">{review.root_cause_summary}</p>
                    </div>
                  )}
                  {review.proposed_solution && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-gray-600">Proposed Solution:</p>
                      <p className="text-sm text-gray-700">{review.proposed_solution}</p>
                    </div>
                  )}
                </div>
              )}

              {/* AI Confidence */}
              {review.ai_confidence_score !== undefined && (
                <div className="flex items-center text-sm">
                  <span className="text-gray-600 mr-2">AI Confidence:</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${review.ai_confidence_score * 100}%` }}
                    />
                  </div>
                  <span className="ml-2 text-gray-700 font-medium">
                    {(review.ai_confidence_score * 100).toFixed(0)}%
                  </span>
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Created</p>
                  <p className="text-gray-900 font-medium">{formatDate(review.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Last Updated</p>
                  <p className="text-gray-900 font-medium">{formatDate(review.updated_at)}</p>
                </div>
                {review.approval_group_id && (
                  <div>
                    <p className="text-gray-600">Approval Group</p>
                    <p className="text-gray-900 font-medium">{review.approval_group_id}</p>
                  </div>
                )}
              </div>

              {/* Comment section */}
              <div>
                <label htmlFor="review-comment" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Comments (optional)
                </label>
                <textarea
                  id="review-comment"
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={isProcessing || !!success}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Add any feedback or notes about this standard..."
                />
              </div>
            </div>
          </div>

          {/* Footer - Action buttons */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => handleReviewAction('rejected')}
                  disabled={isProcessing || !!success}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FiXCircle className="mr-2 h-4 w-4" />
                      Reject
                    </>
                  )}
                </button>

                <button
                  onClick={() => handleReviewAction('changes_requested')}
                  disabled={isProcessing || !!success}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-yellow-100 border border-yellow-300 rounded-lg hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <FiAlertTriangle className="mr-2 h-4 w-4" />
                  Request Changes
                </button>

                <button
                  onClick={() => handleReviewAction('approved')}
                  disabled={isProcessing || !!success}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FiCheckCircle className="mr-2 h-4 w-4" />
                      Approve
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewActionModal;
